import { useState, useEffect, useCallback } from "react"
import type { SelectChangeEvent } from "@mui/material"
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { Cell, Pie, PieChart, Tooltip } from "recharts"
import { Title } from "components/Common/Title"
import { ExamSectionSkeleton } from "./ExamSectionSkeleton"
import { hiabColors } from "theme"
import analyticsService from "services/analyticsService"
import { applyPassRateFilter } from "./analyticsFilters"
import type { PassRateFilterOption } from "./analyticsFilters"
import type { IAnalyticsExamsSummary } from "types/analyticsTypes"

type ViewFilter = PassRateFilterOption

type Props = {
  orgId: string | null
}

export function ExamSummarySection({ orgId }: Props) {
  const [data, setData] = useState<IAnalyticsExamsSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<ViewFilter>("bottom10")

  const fetchData = useCallback(() => {
    setLoading(true)
    setError(null)
    analyticsService
      .getExamsSummary(orgId)
      .then((result) => {
        setData(result)
        setLoading(false)
      })
      .catch(() => {
        setError("Failed to load exam summary data.")
        setLoading(false)
      })
  }, [orgId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleFilterChange = (event: SelectChangeEvent<ViewFilter>) => {
    setFilter(event.target.value as ViewFilter)
  }

  if (loading) return <ExamSectionSkeleton rows={5} tall />

  if (error || !data) {
    return (
      <Paper sx={{ p: 3 }}>
        <Title>EXAM PERFORMANCE SUMMARY</Title>
        <Typography color="error" variant="body2">
          {error ?? "No data available."}
        </Typography>
        <Button size="small" onClick={fetchData} sx={{ mt: 1 }}>
          Retry
        </Button>
      </Paper>
    )
  }

  const totalTaken = data.globalExamsTaken
  const totalPassed = data.globalExamsPassed
  const totalFailed = totalTaken - totalPassed

  const pieData = [
    { name: "Passed", value: totalPassed },
    { name: "Failed", value: totalFailed },
  ]

  const sortedOrgs = [...data.byOrganization].sort((a, b) => a.passRate - b.passRate)
  const filtered = applyPassRateFilter(sortedOrgs, filter)

  return (
    <Paper sx={{ p: 3 }}>
      <Title>EXAM PERFORMANCE SUMMARY</Title>

      {/* Global stats row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 4, mb: 3, flexWrap: "wrap" }}>
        {/* Donut chart */}
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <PieChart width={220} height={220}>
            <Pie
              data={pieData}
              cx={110}
              cy={110}
              innerRadius={60}
              outerRadius={90}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
            >
              <Cell fill={hiabColors.bluePrimary} />
              <Cell fill={hiabColors.redPrimary} />
            </Pie>
            <Tooltip formatter={(value: number, name: string) => [value, name]} />
            <text
              x={110}
              y={110}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ fontSize: 20, fontWeight: "bold", fill: hiabColors.greyPrimary }}
            >
              {data.globalPassRate}%
            </text>
          </PieChart>
        </Box>

        {/* Stat cards */}
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h3" color="primary">
              {data.globalPassRate}%
            </Typography>
            <Typography variant="body2">{orgId ? "Pass Rate" : "Global Pass Rate"}</Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h3" color="primary">
              {totalTaken}
            </Typography>
            <Typography variant="body2">Total Exams Taken</Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h3" color="primary">
              {totalPassed}
            </Typography>
            <Typography variant="body2">Total Passed</Typography>
          </Box>
        </Box>
      </Box>

      {!orgId && (
        <>
          {/* Per-org table with filter */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Pass Rates by Organization
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Show</InputLabel>
              <Select<ViewFilter> value={filter} label="Show" onChange={handleFilterChange}>
                <MenuItem value="bottom10">Bottom 10 (worst pass rates)</MenuItem>
                <MenuItem value="top10">Top 10 (best pass rates)</MenuItem>
                <MenuItem value="all">All ({data.byOrganization.length} orgs)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No organization data available.
            </Typography>
          ) : (
            <Box sx={filter === "all" ? { maxHeight: 400, overflow: "auto" } : {}}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Organization</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>Taken</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>Passed</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>Pass Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((org) => (
                      <TableRow key={org.orgId}>
                        <TableCell>{org.orgName}</TableCell>
                        <TableCell align="right">{org.examsTaken}</TableCell>
                        <TableCell align="right">{org.examsPassed}</TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: org.passRate < 50 ? hiabColors.redPrimary : "inherit" }}
                        >
                          {org.passRate}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      )}
    </Paper>
  )
}
